import Dashboard from '@pages/Dashboard';
import Chat from '@pages/Chat';
import CrudPage from '@pages/CrudPage';
import AgentsPage from '@pages/AgentsPage';
import DynamicToolsPage from '@pages/DynamicToolsPage';
import PrescriptionsPage from '@pages/PrescriptionsPage';
import ToolProvidersPage from '@pages/ToolProvidersPage';
import KnowledgeBasesPage from '@pages/KnowledgeBasesPage';
import ConnectionsPage from '@pages/ConnectionsPage';
import ConnectionCategoriesPage from '@pages/ConnectionCategoriesPage';
import ConnectionProvidersPage from '@pages/ConnectionProvidersPage';
import ModelCatalogPage from '@pages/ModelCatalogPage';
import VoiceSettingsPage from '@pages/VoiceSettingsPage';
import AgentFeedbackPage from '@pages/AgentFeedbackPage';
import VoiceCallsPage from '@pages/VoiceCallsPage';
import VoiceCampaignsPage from '@pages/VoiceCampaignsPage';
import WebhooksPage from '@pages/WebhooksPage';
import ApiTokensPage from '@pages/ApiTokensPage';
import EmbedKeysPage from '@pages/EmbedKeysPage';
import CompaniesPage from '@pages/CompaniesPage';
import WorkflowsPage from '@pages/WorkflowsPage';
import MockApiPage from '@pages/MockApiPage';
import UiAuditsPage from '@pages/UiAuditsPage';
import BrowserDevicesPage from '@pages/BrowserDevicesPage';
import BrowserCommandsPage from '@pages/BrowserCommandsPage';
import ApplicationsPage from '@pages/ApplicationsPage';
import ConnectorsPage from '@pages/ConnectorsPage';
import UsersAdmin from '@pages/admin/UsersAdmin';
import RolesAdmin from '@pages/admin/RolesAdmin';
import SettingsAdmin from '@pages/admin/SettingsAdmin';
import { isMultiTenant, isRetiredModule } from '@/lib/tenantAuth';

const kebab = (s) =>
  String(s)
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .toLowerCase();

// Models with a purpose-built page instead of the generic CrudPage.
const CUSTOM_PAGES = {
  ApiToken: { element: <ApiTokensPage />, label: 'API Tokens' },
  AppAiAgent: { element: <AgentsPage />, label: 'AI Agents' },
  AppEmbedKey: { element: <EmbedKeysPage />, label: 'Embed Keys' },
  AppCompany: { element: <CompaniesPage />, label: 'Companies' },
  AppDynamicTool: { element: <DynamicToolsPage />, label: 'Dynamic Tools' },
  // Default `view` gate, deliberately: App.jsx wraps every route in RequirePermission
  // with this same action, so `run` here would redirect an auditor holding only
  // tools.view.org away from a history they are entitled to read. The page gates the
  // spend-money half separately on tools:run and explains itself if they lack it.
  AppPrescriptionCheck: { element: <PrescriptionsPage />, label: 'Prescriptions' },
  AppToolProvider: { element: <ToolProvidersPage />, label: 'Tool Providers' },
  AppWorkflow: { element: <WorkflowsPage />, label: 'Workflows' },
  AppMockEnvironment: { element: <MockApiPage />, label: 'Mock API' },
  // Default `view` gate: App.jsx wraps every route in RequirePermission with this
  // same action, so `author` here would redirect a reviewer holding only
  // uiaudits.view.org away from findings they are entitled to read. The page gates
  // the start-a-run half separately on uiaudits:author.
  AppUiAudit: { element: <UiAuditsPage />, label: 'UI Audits' },
  AppBrowserDevice: { element: <BrowserDevicesPage />, label: 'Browser Devices' },
  // Default `view` gate, like the device list it sits beside: browser.view.org's own
  // description already covers "the log of commands they were sent", so no narrower action
  // would be right, and App.jsx's RequirePermission would redirect a viewer who is entitled
  // to read this.
  AppBrowserCommand: { element: <BrowserCommandsPage />, label: 'Browser Commands' },
  AppApplication: { element: <ApplicationsPage />, label: 'Applications' },
  AppKnowledgeBase: { element: <KnowledgeBasesPage />, label: 'Knowledge Bases' },
  AppConnection: { element: <ConnectionsPage />, label: 'Connections' },
  AppConnectionCategory: { element: <ConnectionCategoriesPage />, label: 'Connection Categories' },
  AppConnectionProvider: { element: <ConnectionProvidersPage />, label: 'Connection Providers' },
  AppProviderModel: { element: <ModelCatalogPage />, label: 'AI Models' },
  AppAgentFeedback: { element: <AgentFeedbackPage />, label: 'Feedback' },
  AppVoiceCall: { element: <VoiceCallsPage />, label: 'Calls' },
  AppVoiceCallCampaign: { element: <VoiceCampaignsPage />, label: 'Campaigns' },
  AppWebhookEndpoint: { element: <WebhooksPage />, label: 'Webhooks' },
};

/**
 * Build the protected route table from the module registry (pass the live
 * registry from useModules()). Each model gets a generic list page (or its custom
 * override); nav items + route guards key off (module, action='view') — except
 * hand-written pages like Chat, whose real gate is a different action
 * (agents:chat, not agents:view).
 */
export function buildRoutes(modules = {}) {
  const routes = [
    { path: '/', element: <Dashboard />, label: 'Dashboard', icon: 'layout-dashboard', module: 'core', action: 'view' },
    { path: '/chat', element: <Chat />, label: 'Chat', icon: 'message-circle', module: 'agents', action: 'chat' },
    // Not model-backed (no generic CRUD, custom endpoints — services/tts/*.js), so it
    // doesn't fit the per-model loop below; hardcoded here the same way as Dashboard/Chat.
    { path: '/voice-settings', element: <VoiceSettingsPage />, label: 'Voice Settings', icon: 'volume-2', module: 'voice', action: 'view' },
    // Not model-backed (connectors are compile-time definitions, services/chat/tools/
    // connectors.js) — hardcoded here the same way as Voice Settings, so an empty
    // `models: []` on the connectors module doesn't silently drop it from the sidebar.
    { path: '/connectors', element: <ConnectorsPage />, label: 'Connectors', icon: 'puzzle', module: 'connectors', action: 'view' },
  ];
  // Multi-tenant mode: companies live on the platform and applications became branches, so
  // their legacy screens go; company administration (users, roles) comes in their place.
  const multi = isMultiTenant();
  if (multi) {
    routes.push(
      { path: '/admin/users', element: <UsersAdmin />, label: 'Users', icon: 'users', module: 'users', action: 'view' },
      { path: '/admin/roles', element: <RolesAdmin />, label: 'Roles', icon: 'shield', module: 'roles', action: 'view' },
      { path: '/admin/settings', element: <SettingsAdmin />, label: 'Settings', icon: 'settings', module: 'settings', action: 'view' },
    );
  }
  for (const mod of Object.values(modules)) {
    if (isRetiredModule(mod.key)) continue;
    const models = mod.models || [];
    for (const model of models) {
      const custom = CUSTOM_PAGES[model];
      routes.push({
        path: `/${kebab(model)}`,
        element: custom ? custom.element : <CrudPage model={model} title={model} />,
        label: custom ? custom.label : model,
        icon: mod.icon,
        module: mod.key,
        action: (custom && custom.action) || 'view',
        // Multi-page modules (e.g. connections: Connections / Categories / Providers)
        // render as one collapsible sidebar group instead of N flat items
        // (DefaultLayout.jsx nests these via the twreact Sidebar's `children`).
        group: models.length > 1 ? { key: mod.key, label: mod.label, icon: mod.icon } : null,
      });
    }
  }
  return routes;
}
